import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/layout/PageHeader';

// CSV data embedded directly (could also be fetched from backend)
const rbacCSV = `Permission Group;Data Scope;SysAdmin;DevOps;ClientAdmin;ProjectMgr;Engineer;Viewer
NAVIGATION & HOME;;;;;;;
Access Application;-;3;3;3;3;3;1
View Projects List;C;3-A;1-A;3-C;3-P;1-P;1-P
View Sub-projects List;C;3-A;1-A;3-C;3-P;1-SP;1-SP
PROJECT MANAGEMENT;;;;;;;
Create/Delete Projects;C;3-A;0;3-C;3-P;0;0
Edit Project Data;P;3-A;0;3-C;3-P;2-P;0
View Project Data;P;3-A;1-A;3-C;3-P;1-P;1-P
Auto-generate Project Content (AI);P;3-A;0;3-C;3-P;2-P;0
SUB-PROJECT MANAGEMENT;;;;;;;
Create/Delete Sub-projects;P;3-A;0;3-C;3-P;3-SP;0
Edit Sub-project Data;SP;3-A;0;3-C;3-P;2-SP;0
View Sub-project Data;SP;3-A;1-A;3-C;3-P;1-SP;1-SP
Auto-generate Sub-project Content (AI);SP;3-A;0;3-C;3-P;2-SP;0
DOCUMENT MANAGEMENT;;;;;;;
Upload/Delete Documents;P;3-A;0;3-C;3-P;3-SP;0
View Documents;P;3-A;1-A;3-C;3-P;1-P;1-P
Perform OCR;P;3-A;0;3-C;3-P;3-SP;0
Generate Derived Documents;P;3-A;0;3-C;3-P;3-SP;0
SEARCH & PRIOR ART;;;;;;;
Create/Execute Searches;SP;3-A;0;3-C;3-P;3-SP;0
View Search Results;SP;3-A;1-A;3-C;3-P;1-SP;1-SP
View Tokens consumption during search;SP;3-A;1-A;3-C;3-P;1-SP;1-SP
Modify/Delete Searches;SP;3-A;0;3-C;3-P;3-SP;0
Update Content from Search Results;SP;3-A;0;3-C;3-P;2-SP;0
AI/CHATBOT FUNCTIONS;;;;;;;
Ideation Chatbot;SP;3-A;0;3-C;3-P;3-SP;0
Draft Solution/Invention Papers;SP;3-A;0;3-C;3-P;3-SP;0
Generate Programs (Test/Industrial);SP;3-A;0;3-C;3-P;3-SP;0
Patentability Assessment;SP;3-A;0;3-C;3-P;3-SP;0
Freedom to Exploit Analysis;SP;3-A;0;3-C;3-P;3-SP;0
Subject Enrichment/Review Agents;SP;3-A;0;3-C;3-P;3-SP;0
KNOWLEDGE BASE;;;;;;;
Create/Delete Knowledge Bases;C;3-A;0;3-C;3-P;3-O;0
View Knowledge Bases;C;3-A;1-A;3-C;3-P;1-C;1-P
Add/Remove Documents in KB;O;3-A;0;3-C;3-P;3-O;0
USER MANAGEMENT;;;;;;;
View Users (All Clients);-;3-A;1-A;0;0;0;0
View Users (Own Client);C;3-A;0;3-C;1-C;0;0
Create/Modify/Delete Users;C;3-A;0;3-C;0;0;0
Assign Users to Projects;P;3-A;0;3-C;3-P;0;0
MONITORING;;;;;;;
System Monitoring;-;3-A;3-A;0;0;0;0
Project Monitoring (All);-;3-A;1-A;0;0;0;0
Project Monitoring (Own Client);C;3-A;0;3-C;1-P;0;0
Token Consumption Reports;C;3-A;1-A;3-C;1-P;0;0
SETTINGS;;;;;;;
Application Settings;-;3-A;3-A;0;0;0;0
Client Settings;C;3-A;0;3-C;0;0;0
User Settings (Own);O;3-O;3-O;3-O;3-O;3-O;3-O
Language Preferences;O;3-O;3-O;3-O;3-O;3-O;3-O
BACKEND/SYSTEM;;;;;;;
User Authentication;-;3-A;3-A;0;0;0;0
Database Management;-;3-A;3-A;0;0;0;0
Execute Search Services (EPO etc.);-;3-A;3-A;0;0;0;0`;

const rbacLegend = `SIMPLIFIED RBAC MATRIX - LEGEND & IMPLEMENTATION GUIDE
======================================================

ROLES (6):
----------
1. SysAdmin      - System Administrator
2. DevOps        - Developer/Operations
3. ClientAdmin   - Client Administrator
4. ProjectMgr    - Project Manager
5. Engineer      - R&D Engineer
6. Viewer        - Read-only User

DATA SCOPE VALUES:
------------------
A   = All data (system-wide)
C   = Client data only
P   = Assigned projects only
SP  = Assigned sub-projects only
O   = Own/created data only
-   = No scope (applies to all)

PERMISSION LEVELS:
------------------
0 = None (no access)
1 = View only
2 = View + Edit
3 = Full (View + Edit + Delete + Create)

PERMISSION FORMAT:
------------------
Format: [Level]-[Scope]
Examples:
  3-A  = Full access to All data
  3-C  = Full access to Client data only
  1-P  = View only for assigned Projects
  2-SP = View+Edit for assigned Sub-Projects
  3-O  = Full access to Own data only

ROLE MIGRATION FROM OLD SYSTEM:
--------------------------------
Old Role                → New Role
App_admin              → SysAdmin
App_dev                → DevOps
Clt_Admin              → ClientAdmin
Clt_Project_Head       → ProjectMgr
Clt_Project_User       → Engineer (assigned to projects)
Clt_SubProject_Head    → ProjectMgr or Engineer (with broader scope)
Clt_SubProject_User    → Engineer (assigned to specific sub-projects)

KEY IMPROVEMENTS:
-----------------
- From 83 functions → 45 permission groups
- From 7 roles → 6 roles
- Data scope layer prevents role explosion
- Clearer separation between system/client/project/resource ownership

IMPLEMENTATION ALGORITHM:
-------------------------
def check_permission(user, resource, action):
    """
    Check if user has permission to perform action on resource
    """
    # 1. Get user role
    role = user.user_type

    # 2. Get permission level for action from matrix
    permission_level = MATRIX[action][role]

    # 3. If no access, deny immediately
    if permission_level == 0:
        return False

    # 4. Get data scope for this action+role
    scope = DATA_SCOPE[action][role]

    # 5. Apply data scope filter
    if scope == 'A':
        return True  # System-wide access
    elif scope == 'C':
        return resource.client_id == user.client_id
    elif scope == 'P':
        return resource.project_id in user.assigned_projects
    elif scope == 'SP':
        return resource.subproject_id in user.assigned_subprojects
    elif scope == 'O':
        return resource.owner_id == user.id
    elif scope == '-':
        return True  # No scope restriction

    return False

USER ASSIGNMENT RULES:
----------------------
1. SysAdmin:
   - Manually assigned (1-2 users max)
   - Has access to all data across all clients

2. DevOps:
   - Manually assigned for technical staff
   - Can view all data for debugging but cannot modify business data

3. ClientAdmin:
   - One per client (stored in Client.admin_user_id)
   - Manages all users and projects for their client

4. ProjectMgr:
   - Assigned via Project.head_of_project field
   - Can manage the specific project and all its sub-projects
   - Can assign Engineers to project

5. Engineer:
   - Assigned via Project.project_engineers[] array
   - OR assigned via SubProject.subproject_engineers[] array
   - Can work on assigned projects/sub-projects
   - user.assigned_projects = list of project IDs where user appears in project_engineers[]
   - user.assigned_subprojects = list of subproject IDs where user appears in subproject_engineers[]

6. Viewer:
   - Assigned same way as Engineer but with view-only role
   - For stakeholders who need to monitor progress

DATABASE SCHEMA UPDATES REQUIRED:
----------------------------------
User table changes:
  - user_type: ENUM('SysAdmin', 'DevOps', 'ClientAdmin', 'ProjectMgr', 'Engineer', 'Viewer')
  - Remove old user types

Add computed fields (can be cached or computed on-the-fly):
  - user.assigned_projects (array of project IDs)
  - user.assigned_subprojects (array of subproject IDs)

FRONTEND IMPLEMENTATION:
------------------------
1. On login, fetch user role and assigned projects/subprojects
2. Store in session/context
3. UI should hide/disable features based on permissions
4. Backend must always validate permissions (don't trust frontend)

EXAMPLE USE CASES:
------------------
Case 1: Engineer tries to delete a project
  - role = 'Engineer'
  - action = 'Create/Delete Projects'
  - permission_level = 0 (from matrix)
  - Result: DENIED

Case 2: ProjectMgr views sub-project data for their project
  - role = 'ProjectMgr'
  - action = 'View Sub-project Data'
  - permission_level = 3
  - scope = 'P' (project scope)
  - Check: subproject.project_id in user.assigned_projects
  - Result: ALLOWED if project matches

Case 3: Engineer edits sub-project they're assigned to
  - role = 'Engineer'
  - action = 'Edit Sub-project Data'
  - permission_level = 2
  - scope = 'SP' (sub-project scope)
  - Check: subproject.id in user.assigned_subprojects
  - Result: ALLOWED if assigned

Case 4: ClientAdmin views users in their client
  - role = 'ClientAdmin'
  - action = 'View Users (Own Client)'
  - permission_level = 3
  - scope = 'C' (client scope)
  - Check: users.client_id == user.client_id
  - Result: ALLOWED, sees only their client's users`;

interface RBACRow {
  permissionGroup: string;
  dataScope: string;
  roles: { [key: string]: string };
  isHeader: boolean;
}

const SettingsRBAC: React.FC = () => {
  const { t } = useTranslation();
  const [rbacData, setRbacData] = useState<RBACRow[]>([]);
  const [roleHeaders, setRoleHeaders] = useState<string[]>([]);

  useEffect(() => {
    parseCSV();
  }, []);

  const parseCSV = () => {
    const lines = rbacCSV.trim().split('\n');
    const headers = lines[0].split(';');
    
    // Extract role headers (skipping first two columns)
    const roles = headers.slice(2);
    setRoleHeaders(roles);

    const parsedData: RBACRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const values = line.split(';');
      
      // Check if this is a section header (all role columns are empty)
      const isHeader = values.slice(2).every(v => !v || v.trim() === '');
      
      if (isHeader && values[0].trim()) {
        parsedData.push({
          permissionGroup: values[0],
          dataScope: values[1] || '',
          roles: {},
          isHeader: true
        });
      } else if (values[0].trim()) {
        const roleValues: { [key: string]: string } = {};
        roles.forEach((role, idx) => {
          roleValues[role] = values[idx + 2] || '0';
        });

        parsedData.push({
          permissionGroup: values[0],
          dataScope: values[1] || '-',
          roles: roleValues,
          isHeader: false
        });
      }
    }

    setRbacData(parsedData);
  };

  const getPermissionClass = (value: string): string => {
    if (!value || value === '0') return 'bg-slate-50 text-slate-300';
    if (value.startsWith('3')) return 'bg-green-50 text-green-700 font-medium';
    if (value.startsWith('2')) return 'bg-blue-50 text-blue-700';
    if (value.startsWith('1')) return 'bg-amber-50 text-amber-700';
    return 'text-slate-300';
  };

  const getPermissionTitle = (value: string): string => {
    if (!value || value === '0') return t('pages.settingsRBAC.noAccess');
    const level = value.charAt(0);
    const scope = value.length > 1 ? value.substring(2) : '';
    
    let levelText = '';
    switch (level) {
      case '1': levelText = t('pages.settingsRBAC.viewOnly'); break;
      case '2': levelText = t('pages.settingsRBAC.viewEdit'); break;
      case '3': levelText = t('pages.settingsRBAC.fullAccess'); break;
      default: return value;
    }

    if (scope) {
      const scopeMap: { [key: string]: string } = {
        'A': t('pages.settingsRBAC.allData'),
        'C': t('pages.settingsRBAC.clientData'),
        'P': t('pages.settingsRBAC.assignedProjects'),
        'SP': t('pages.settingsRBAC.assignedSubProjects'),
        'O': t('pages.settingsRBAC.ownData')
      };
      return `${levelText} (${scopeMap[scope] || scope})`;
    }
    return levelText;
  };

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader title="RBAC" subtitle={t('pages.settingsRBAC.intro')} />

      <div className="px-6 pt-4 pb-6">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-600 min-w-[200px]">Permission group</th>
                <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-600 w-12">Data scope</th>
                {roleHeaders.map(role => (
                  <th key={role} className="text-center py-2 px-2 text-xs font-semibold text-slate-600 w-24">{role}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rbacData.map((row, idx) => {
                if (row.isHeader) {
                  return (
                    <tr key={idx} className="bg-slate-100">
                      <td colSpan={roleHeaders.length + 2} className="py-1.5 px-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                        {row.permissionGroup}
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-1.5 pr-4 text-xs text-slate-700">{row.permissionGroup}</td>
                    <td className="py-1.5 pr-4 text-xs font-mono text-slate-400">{row.dataScope}</td>
                    {roleHeaders.map(role => {
                      const value = row.roles[role] || '0';
                      return (
                        <td key={role} className="py-1.5 px-1 text-center">
                          <span
                            className={`inline-block px-1.5 rounded text-xs ${getPermissionClass(value)}`}
                            title={getPermissionTitle(value)}
                          >
                            {value === '0' ? '—' : value}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <details className="mt-4 group">
          <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600 select-none">
            {t('pages.settingsRBAC.legend')} ▸
          </summary>
          <pre className="mt-2 text-xs text-slate-500 bg-slate-50 rounded p-3 overflow-x-auto leading-relaxed">{rbacLegend}</pre>
        </details>
      </div>
    </div>
  );
};

export default SettingsRBAC;
